/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useCurrentGlobalGroupId} from '@ion/applications/src/hooks/useCurrentGlobalGroupId';
import {useGridColumnPersistence} from '@ion/applications/src/hooks/useGridColumnPersistence';
import {useGridRowsPerPage} from '@ion/applications/src/hooks/useGridRowsPerPage';
import {useViewSubGroups} from '@ion/applications/src/hooks/useViewSubGroups';
import {getEntityDeleteMessage} from '@ion/applications/src/utils/translation';
import useHasMounted from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {IApplicationFeatures} from '@ion/core/src/security/ApplicationFeatures';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  AccountLink,
  AdminGroupsFilters,
  CRUDPermissions,
  Entity,
  EntityStatus,
  GenericObject
} from '@ion/core/src/types/entities';
import {
  DownloadTemplate
} from '@ion/core/src/types/templates';
import {
  AuditDateFilterProps,
  AuditDialog
} from '@ion/dls/src/editors/audit/AuditDialog';
import {
  ViewAuditChangesDialog
} from '@ion/dls/src/editors/audit/ViewAuditChangesDialog';
import {GroupsDeletePrompt} from '@ion/dls/src/editors/GroupsDeletePrompt';
import {SFTPEditorEnablement} from '@ion/dls/src/editors/SFTPConfigurationEditor';
import useShowDeletePrompt from '@ion/dls/src/hooks/useShowDeletePrompt';
import {
  ModularSearchProps,
  PageViewSubGroupsProps,
  ViewSubGroupsPosition
} from '@ion/dls/src/types/types';
import {getViewSubGroupsProps} from '@ion/dls/src/utils/ComponentUtils';
import DeleteResultPrompt from '@ion/features/src/prompts/DeleteResultPrompt';
import BaseSearchMapping from '@ion/features/src/search/BaseSearchMappings';
import {
  AttributeOperators,
  AttributeTypes,
  DISABLED,
  ENABLED,
  Mapping,
  SuggestionsType,
  SUSPENDED
} from '@ion/features/src/search/types';
import {getQueryClient} from '@ion/query/src/config/client';
import QueryKey from '@ion/query/src/config/QueryKeys';
import useDeleteSSOConfiguration from '@ion/query/src/hooks/useDeleteSSOConfiguration';
import {ExportContext} from '../../../types/redux';
import {ReportOperations} from '../../reports/reportsUtils';
import AdminContainer, {
  GridOperations,
  PageTitle
} from '../AdminContainer';
import AddGroupEditor, {AddAccountProps} from './AddGroupEditor';
import AddSSOEditor from './AddSSOEditor';
import AdminGroupsGrid, {GroupsGridColumns} from './AdminGroupsGrid';
import EditGroupEditor from './EditGroupEditor';
import EditSSOEditor from './EditSSOEditor';
import {
  addAdminGroup,
  auditAdminGroup,
  deleteAdminGroup,
  deleteGroup,
  editAdminGroup,
  importAdminGroups,
  initializeAdminGroups,
  NAME,
  PREFERENCE,
  setAdminGroupsGridPageSize,
  setAdminGroupsViewSubGroups,
  showAuditChangesDialog,
  UPDATE_GRID_COLUMNS,
  updateAdminGroupsSearchQueries
} from './state';
import {addSSOShowDialogSelector, editSSOShowDialogSelector} from './state-selectors';

// defines the prop definitiions for the AdminGroups
export interface AdminGroupsProps extends ReportOperations,
  GridOperations,
  PageViewSubGroupsProps,
  AuditDateFilterProps,
  AddAccountProps,
  ModularSearchProps,
  SFTPEditorEnablement {
  onSelectAccount?: (value: AccountLink) => void;
  accountHierarchy?: AccountLink[];
  refreshInitialization?: Function;
  Features: IApplicationFeatures;
  isCtcAdmin: boolean;
  isChipView?: boolean;
  allowSSOConfig?: boolean;
  allowDeleteSSOConfig?: boolean;
  allowUpdateSSOConfig?: boolean;
  includeContacts?: boolean;
  includeAddresses?: boolean;
  includeApplications?: boolean;
  includeAccountContacts?: boolean;
  includeCurrentAccountEdit?: boolean;
  includeGroupProperties?: boolean;
  includeAccountLink?: boolean;
  includeGlobalSearch?: boolean;
  searchKeyword?: string;
  defaultAccount?: AccountLink;
  filteredColumns?: GroupsGridColumns[];
  accountsPermissions?: CRUDPermissions;
  applicationPermissions?: CRUDPermissions;
  customVisionPermissions?: CRUDPermissions;
  hasReadPermission?: boolean;
  hasUpdatePermission?: boolean;
  hasDeletePermission?: boolean;
  usePreferedPageSize?: boolean;
  hasAutoJobs?: boolean;
  includeParentInExport?: boolean;
  hasCarbFeature?: boolean;
};

// [ION-APP TODO]: Use Hooks to refactor this operation of modular search.
// Reference: packages/ion-ctc-admin/src/routes/provisioning/devicefiles/DeviceFiles.tsx
// Defines the searchable attributes, options, and labels for modularized search.
const AccountMappings = (): {[key: string]: Mapping} => ({
  name : {
    type       : AttributeTypes.TEXT,
    attributes : {
      value : AttributeOperators.CONTAINS,
      label : translate('common:modularsearch.attributes.contains')
    },
    suggestion : SuggestionsType.TEXT,
    label      : translate('admin-groups:modularSearch.labels.name'),
    key        : 'name'
  },
  id : {
    type       : AttributeTypes.TEXT,
    attributes : {
      value : AttributeOperators.IS,
      label : translate('common:modularsearch.attributes.is')
    },
    suggestion      : SuggestionsType.TEXT,
    label           : translate('admin-groups:modularSearch.labels.accountId'),
    key             : 'id',
    hasNumericValue : true
  },
  status : {
    type       : AttributeTypes.SELECT,
    attributes : {
      value : AttributeOperators.IN,
      label : translate('common:modularsearch.attributes.in')
    },
    options : [
      {
        label : translate('common:modularsearch.status.Enabled'),
        value : ENABLED
      },
      {
        label : translate('common:modularsearch.status.Disabled'),
        value : DISABLED
      },
      {
        label : translate('common:modularsearch.status.Suspended'),
        value : SUSPENDED
      }
    ],
    suggestion : SuggestionsType.SELECTION,
    label      : translate('admin-groups:modularSearch.labels.status'),
    key        : 'status'
  },
  solomonId : {
    type       : AttributeTypes.TEXT,
    attributes : {
      value : AttributeOperators.CONTAINS,
      label : translate('common:modularsearch.attributes.contains')
    },
    suggestion : SuggestionsType.TEXT,
    label      : translate('admin-groups:modularSearch.labels.solomonId'),
    key        : 'solomonId'
  }
});

// Defines the potential search types for modularized search.
const uniqueConditions = (): string =>
  (`(${translate('common:modularsearch.attributes.contains')}|${translate('common:modularsearch.attributes.in')})`);

/**
 * Defines the AdminGroups container component. This component is only to render
 * child components and provide top level subscriptions and dispatches to the
 * redux store.
 */
export const AdminGroups = ({
  Features,
  isCtcAdmin,
  includeApplications,
  includeCurrentAccountEdit,
  refreshInitialization,
  onSelectAccount,
  accountHierarchy,
  defaultAccount,
  filteredColumns,
  accountsPermissions,
  applicationPermissions,
  customVisionPermissions,
  searchKeyword = '',
  hasCarbFeature = false,
  includeSFTPEditor = false,
  isChipView = false,
  allowSSOConfig = false,
  allowDeleteSSOConfig = false,
  allowUpdateSSOConfig = false,
  includeAccountLink = true,
  includeGlobalSearch = false,
  isSortByAlphaNumeric = false,
  enableSubgroupToggle = false,
  enableDateFilter = false,
  enableRangeSelection = false,
  includeIonAccount = false,
  usePreferedPageSize = false,
  autoSelectNext = false,
  hasAutoJobs = false,
  includeParentInExport = false,
  viewSubGroupsPosition = ViewSubGroupsPosition.GRID_PAGINATION_CONTROLS
}: AdminGroupsProps) => {
  const dispatch                            = useDispatch();
  const isMounted                           = useHasMounted();
  const [totalRowsCount, setTotalRowsCount] = useState<number>(0);
  const {
    result,
    selected,
    isEditing,
    loading,
    error
  }                               = useSelector(({adminGroups: {destroy}}) => destroy);
  const {isEditing: showAddGroup} = useSelector(({adminGroups: {add}}) => add);
  const {
    isEditing: showEditGroup,
    isViewMode: showViewGroup
  }                               = useSelector(({adminGroups: {edit}}) => edit);

  const {
    isEditing: showAuditGroup,
    selected: auditGroup
  }                               = useSelector(({adminGroups: {audit}}) => audit);
  const {
    auditRecord: selectedAuditRecord,
    showChangesDialog: shouldShowAuditChangesDialog
  } = useSelector(({adminGroups: {audit}}) => audit);

  const showAddSSODialog          = useSelector(addSSOShowDialogSelector);
  const showEditSSODialog         = useSelector(editSSOShowDialogSelector);

  const gridRef                   = useRef<GenericObject>();
  const {columnState}             = useSelector(({adminGroups: {grid}}) => grid);
  const currentGlobalGroupId      = useCurrentGlobalGroupId();
  const isLoggedinUserAccount     = entityId(defaultAccount) === currentGlobalGroupId;
  const ssoConfigId               = entityId(result?.ssoConfig, false);

  const {hasUpdatePermission: hasCustomVisionUpdatePermission} = customVisionPermissions || {};

  const {
    deleteSSOConfig,
    isLoading,
    isError,
    isSuccess
  } = useDeleteSSOConfiguration();

  const {
    setShowError,
    setShowSuccess,
    showSuccess,
    showError,
    closeSnack
  } = useShowDeletePrompt();

  const [viewSubGroups, toggleViewSubGroups] = useViewSubGroups({
    name       : NAME,
    action     : setAdminGroupsViewSubGroups,
    view       : enableSubgroupToggle,
    preference : PREFERENCE
  });

  const viewSubGroupsProps = useMemo(() => getViewSubGroupsProps(
    viewSubGroupsPosition,
    viewSubGroups as boolean,
    toggleViewSubGroups as Function
  ), [viewSubGroupsPosition, viewSubGroups, toggleViewSubGroups]);

  const [gridPageSize, setGridPageSize] = useGridRowsPerPage({
    name       : NAME,
    action     : setAdminGroupsGridPageSize,
    preference : PREFERENCE
  });

  useEffect(() => {
    if (!isMounted) {
      dispatch(initializeAdminGroups());
    }
  }, [isMounted]);

  /**
   * If an account is deleted, the associated `SSO Configuration` linked to that account
   * should also be deleted.
   */
  useEffect(() => {
    if (allowDeleteSSOConfig && result?.status === EntityStatus.Deleted && !!ssoConfigId) {
      setTimeout(() => {
        deleteSSOConfig(ssoConfigId);
      }, 1000);
    }
  }, [result, ssoConfigId, isEditing]);

  useEffect(() => {
    if (isError && !isLoading) {
      setShowError(isError);
    }
  }, [isError, isLoading]);

  useEffect(() => {
    if (isSuccess && !isLoading) {
      setShowSuccess(isSuccess);
    }
  }, [isSuccess, isLoading]);

  /**
   * @private
   */
  const onImport = function() {
    dispatch(importAdminGroups());
  };

  /**
   * @private
   */
  const onExport = function(downloadParams?: DownloadTemplate) {
    gridRef?.current?.onExport?.(downloadParams);
  };

  /**
  * @private
  */
  const onRefresh = function() {
    getQueryClient().invalidateQueries(QueryKey.MESSAGE_ENDPOINT);
    gridRef.current?.onRefresh(true);
  };

  /**
   * @private
   */
  function onToggleAddForm() {
    dispatch(addAdminGroup());
  };

  /**
   * @private
   */
  const onDeleteConfirm = function() {
    dispatch(deleteGroup(selected, refreshInitialization));
  };

  /**
   * @private
   */
  function onDeleteCancel() {
    dispatch(deleteAdminGroup(selected, false));
  }

  /**
   * @private
   */
  function onAuditClose() {
    dispatch(auditAdminGroup(null, false));
  }

  /**
   * @private
   */
  const onUpdateGridColumns = useGridColumnPersistence({
    action : UPDATE_GRID_COLUMNS,
    name   : NAME,
    columnState
  });

  const getDeleteMessage = function() {
    if (isCtcAdmin && selected) {
      return getEntityDeleteMessage(selected);
    }
  };

  function onViewAuditChanges(auditRecord, show = false) {
    dispatch(showAuditChangesDialog(auditRecord, show));
  };

  function onViewAuditChangesClose() {
    dispatch(showAuditChangesDialog(null, false));
  };

  const editGroup = useCallback(function(accountId: number, isEdit: boolean = true) {
    dispatch(editAdminGroup({
      account    : accountId,
      show       : true,
      isViewMode : isEdit
    }));
  }, []);

  // Dispatches the given search params to the search redux state for modularized search.
  const getSearchParams = useCallback(function(filters: AdminGroupsFilters) {
    dispatch(updateAdminGroupsSearchQueries(filters));
    onRefresh();
  }, []);

  const mappings = useMemo(() => {
    return new BaseSearchMapping(AccountMappings(), uniqueConditions(), currentGlobalGroupId);
  }, [currentGlobalGroupId]);

  return (
    <>
      <AdminContainer reducer={NAME}
        title={isCtcAdmin ? PageTitle.adminAccounts : PageTitle.adminGroups}
        preference={PREFERENCE}
        isCtcAdmin={isCtcAdmin}
        autoSelectNext={autoSelectNext}
        accountsPermissions={accountsPermissions}
        hasAddPermission={accountsPermissions?.hasAddPermission}
        grid={<AdminGroupsGrid ref={gridRef}
          Features={Features}
          accountsPermissions={accountsPermissions}
          isSortByAlphaNumeric={isSortByAlphaNumeric}
          filteredColumns={filteredColumns}
          isCtcAdmin={isCtcAdmin}
          usePreferedPageSize={usePreferedPageSize}
          includeAccountLink={includeAccountLink}
          includeGlobalSearch={includeGlobalSearch}
          includeParentInExport={includeParentInExport}
          setTotalRowsCount={setTotalRowsCount}
          allowSSOConfig={allowSSOConfig}
          allowUpdateSSOConfig={allowUpdateSSOConfig}
          searchKeyword={searchKeyword}
          onSelectAccount={onSelectAccount}
          accountHierarchy={accountHierarchy}
          refreshInitialization={refreshInitialization}
          gridPageSize={gridPageSize}
          setGridPageSize={setGridPageSize}
          {...viewSubGroupsProps[ViewSubGroupsPosition.GRID_PAGINATION_CONTROLS]} />}
        searchLabel={translate('admin-groups:search')}
        onShowColumnChooser={gridRef.current?.toggleColumnChooser}
        exportContext={ExportContext.account}
        onImport={onImport}
        onExport={onExport}
        onRefresh={onRefresh}
        getSearchParams={getSearchParams}
        searchInstance={mappings}
        onAdd={onToggleAddForm}
        onUpdateGridColumns={onUpdateGridColumns}
        editGroup={editGroup}
        isLoggedinUserAccount={isLoggedinUserAccount}
        includeCurrentAccountEdit={includeCurrentAccountEdit}
        includeGlobalSearch={includeGlobalSearch}
        hasNoRowSelection={totalRowsCount === 0}
        disableExport={totalRowsCount === 0}
        includeExport
        {...viewSubGroupsProps[ViewSubGroupsPosition.GRID_CTAS]} />
      {showAddGroup && <AddGroupEditor isCtcAdmin={isCtcAdmin}
        hasCarbFeature={hasCarbFeature}
        isViewDialog={showViewGroup}
        defaultAccount={defaultAccount}
        accountsPermissions={accountsPermissions}
        applicationPermissions={applicationPermissions}
        hasCustomVisionPermission={hasCustomVisionUpdatePermission}
        includeApplications={includeApplications}
        refreshInitialization={refreshInitialization}
        includeIonAccount={includeIonAccount} />}
      {(showEditGroup || showViewGroup) && (
        <EditGroupEditor isCtcAdmin={isCtcAdmin}
          hasCarbFeature={hasCarbFeature}
          allowSSOConfig={allowSSOConfig}
          allowDeleteSSOConfig={allowDeleteSSOConfig}
          allowUpdateSSOConfig={allowUpdateSSOConfig}
          hasCustomVisionPermission={hasCustomVisionUpdatePermission}
          applicationPermissions={applicationPermissions}
          refreshInitialization={refreshInitialization}
          includeApplications={includeApplications}
          accountsPermissions={accountsPermissions}
          includeSFTPEditor={includeSFTPEditor}
          defaultAccount={defaultAccount}
          isViewDialog={showViewGroup}
          hasAutoJobs={hasAutoJobs}
          isChipView={isChipView} />
      )}
      {showAuditGroup &&
      <AuditDialog enableDateFilter={enableDateFilter}
        item={auditGroup}
        entity={Entity.account}
        onCloseDialog={onAuditClose}
        rowMenusEnabled={isCtcAdmin}
        showDifferences={isCtcAdmin}
        enableRangeSelection={enableRangeSelection}
        onViewAuditChanges={onViewAuditChanges} />}
      {shouldShowAuditChangesDialog && selectedAuditRecord && (
        <ViewAuditChangesDialog auditRecord={selectedAuditRecord}
          onCloseDialog={onViewAuditChangesClose}
          entity={Entity.account} />
      )}
      <GroupsDeletePrompt show={isEditing}
        onDeleteConfirm={onDeleteConfirm}
        onDeleteCancel={onDeleteCancel}
        overrideMessage={getDeleteMessage}
        group={selected}
        result={result}
        loading={loading}
        isCtcAdmin={isCtcAdmin}
        error={error} />
      {showAddSSODialog && (
        <AddSSOEditor refreshInitialization={refreshInitialization} />
      )}
      {showEditSSODialog && (
        <EditSSOEditor refreshInitialization={refreshInitialization}
          isView={!accountsPermissions?.hasUpdatePermission} />
      )}
      <DeleteResultPrompt entityTitle={translate('common:entityLabels.ssoConfiguration')}
        isError={showError}
        isSuccess={showSuccess}
        onAction={closeSnack} />
    </>
  );
};

export default AdminGroups;
